import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { usePagination } from '../../hooks/usePagination';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';

const ProductCategory = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canProductCategory = (key) => can(key, 'productCategory');

  // Pagination
  const pagination = usePagination(10);

  // Search
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  // Selection
  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  // Date filter
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Data and form state
  const [productCategories, setProductCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    serviceIds: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  // Fetch product categories
  const fetchProductCategories = async (searchValue = search) => {
    try {
      const response = await api.get(`/md/product-category-page`, {
        params: {
          page: pagination.page,
          size: pagination.size,
          search: searchValue,
          from: fromDate,
          to: toDate
        }
      });
      setProductCategories(response.data.content);
      pagination.setTotalPages(response.data.totalPages);
      pagination.setTotal(response.data.totalElements);
      pagination.setCurrentPageItems(response.data.content.length);
    } catch (error) {
      let errorMessage = 'Failed to fetch product categories.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view product categories.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching product categories.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching product categories.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setProductCategories([]);
    }
  };

  // Fetch Services
  const fetchServices = async () => {
    try {
      const response = await api.get('/md/company-service-page'); // Updated endpoint to match Services.js
      setServices(response.data.content); // Get content from paginated response
    } catch (error) {
      let errorMessage = 'Failed to fetch services.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view services.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server error while fetching services.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching services.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setServices([]);
    }
  };

  useEffect(() => {
    fetchProductCategories(appliedSearchTerm);
    fetchServices();
  }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

  // Open modal for create
  const handleCreate = () => {
    if (!canProductCategory('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    setFormData({
      name: '',
      serviceIds: []
    });
    setEditingId(null);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-product-category'));
    modal.show();
  };
  const handleEdit = (item) => {
    if (!canProductCategory('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    setFormData({
      name: item.name,
      serviceIds: item.services?.id || ''
    });
    setEditingId(item.id);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-product-category'));
    modal.show();
  };
  

  // Real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'name') {
      error = validateField('name', value, { required: true });
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };
  const handleSelectChange = (name, value) => {
    let parsedValue = value;
  
    // Jika dari TomSelect bentuknya object { id: 1, name: 'Service A' }
    if (name === 'serviceIds') {
      if (typeof value === 'object' && value !== null) {
        parsedValue = parseInt(value.id || value.value);
      } else if (Array.isArray(value)) {
        parsedValue = parseInt(value[0]); // fallback jika value array
      } else {
        parsedValue = parseInt(value);
      }
    }
  
    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue
    }));
  
    let error = null;
    if (name === 'serviceIds') {
      error = parsedValue ? null : 'Service is required';
    }
  
    setErrors((prev) => ({
      ...prev,
      [name]: error
    }));
  };

  
  // Unified handleSubmit for both create and edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    let error = validateField('name', formData.name, { required: true });
    let serviceError = !formData.serviceIds ? 'Service is required' : null;


    setErrors({
      name: error,
      serviceIds: serviceError
    });

    if (error || serviceError) return;

    try {
      const payload = {
        id: editingId,
        name: formData.name,
        // service_id: formData.serviceIds
        services: {
          id: formData.serviceIds
        }
      };

      // alert(JSON.stringify(payload))

      if (editingId) {
        await api.put(`/md/product-category/${editingId}`, payload);
        setSuccessMessage('Product category has been successfully updated.');
      } else {
        await api.post('/md/product-category', payload);
        setSuccessMessage('Product category has been successfully added.');
      }
      setEditingId(null);
      setFormData({
        name: '',
        serviceIds: []
      });
      setErrors({});
      await fetchProductCategories();
      const modal = document.getElementById('modal-product-category');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to save product category.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid product category data provided.';
            technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 409:
            errorMessage = 'A product category with this name already exists.';
            technicalDetails = `Duplicate entry for name: "${formData.name}"`;
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the product category.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  // Edit selected (only works for single selection)
  const editSelected = () => {
    if (selectedIds.length !== 1) {
      setSuccessMessage('Please select exactly one product category to edit');
      setShowSuccessModal(true);
      return;
    }
    const selectedId = selectedIds[0];
    const selectedItem = productCategories.find(item => item.id === selectedId);
    if (selectedItem) {
      handleEdit(selectedItem);
    }
  };

  // Delete selected (bulk)
  const deleteSelected = () => {
    if (!canProductCategory('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (selectedIds.length === 0) {
      setFailMessage('Please select at least one product category to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        await api.delete('/md/product-category/bulk', { data: selectedIds });
        setSelectedIds([]);
        fetchProductCategories();
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected product categories deleted successfully');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete product categories.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more product categories not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'services', label: 'Services', sortable: false, render: (value) => Array.isArray(value) ? value.map(s => s.name).join(', ') : (value ? value.name : '-') },
    { key: 'productLevels', label: 'Product Levels', sortable: false, render: (value) => Array.isArray(value) ? value.map(l => l.name).join(', ') : '-' },
    { key: 'cuser', label: 'Created By', sortable: false, render: (value, row) => row.cuser?.username || '-' },
    { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleDateString() : '-' },
  ];

  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Product Category</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                  onClick={handleCreate}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24"
                    strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <GenericView
                data={productCategories}
                columns={columns}
                isLoading={capabilitiesLoading}
                emptyStateMessage="No product categories found"
                checkboxSelection={true}
                selectedRows={selectedIds}
                onSelectRow={(itemId, isSelected) => {
                  if (isSelected) {
                    setSelectedIds([...selectedIds, itemId]);
                  } else {
                    setSelectedIds(selectedIds.filter(id => id !== itemId));
                  }
                }}
                onSelectAllRows={(itemIds) => {
                  if (Array.isArray(itemIds) && itemIds.length > 0) {
                    setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                  } else {
                    setSelectedIds([]);
                  }
                }}
                onEdit={canProductCategory('edit') ? (item) => {
                  setSelectedIds([item.id]);
                  handleEdit(item);
                } : undefined}
                onDelete={canProductCategory('delete') ? (item) => {
                  setSelectedIds([item.id]);
                  deleteSelected();
                } : undefined}
                searchComponent={
                  <div className="d-flex gap-2">
                    <SearchInput
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchSubmit();
                        }
                      }}
                      placeholder="Search..."
                      uniqueId="product-category-search"
                    />
                    <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                      Search
                    </button>
                  </div>
                }
                actionButtons={
                  <div className="d-flex gap-2">
                    {(canProductCategory('edit') || canProductCategory('delete')) && (
                      <div ref={actionsContainerRef} className="dropdown">
                        <a
                          ref={actionsToggleRef}
                          href="#"
                          className="btn dropdown-toggle"
                          data-bs-toggle="dropdown"
                          aria-expanded={isActionsOpen}
                          onClick={(e) => {
                            e.preventDefault();
                            const inst = actionsDropdownInstanceRef.current;
                            if (inst?.toggle) inst.toggle();
                            else setIsActionsOpen(prev => !prev);
                          }}
                        >Actions</a>
                        <div className="dropdown-menu">
                          {canProductCategory('edit') && (
                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                              style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                              Edit
                            </a>
                          )}
                          {canProductCategory('delete') && (
                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                              style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                              Delete
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                }
                pagination={{
                  currentPage: pagination.page + 1,
                  totalRows: pagination.total,
                  rowsPerPage: pagination.size,
                  onPageChange: (page) => pagination.setPage(page - 1),
                  onRowsPerPageChange: (size) => {
                    pagination.setSize(size);
                    pagination.setPage(0);
                  }
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                uniqueId="product-category-table"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal for add/edit product category */}
      <div className="modal modal-blur fade" id="modal-product-category" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editingId ? 'Edit Product Category' : 'Add New Product Category'}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter name"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <TomSelect
                    name="serviceIds"
                    value={formData.serviceIds}
                    onChange={handleSelectChange}
                    options={services.map(service => ({ id: service.id, name: service.name }))}
                    placeholder="Select Services"
                    label="Services"
                    required={true}
                    error={errors.serviceIds}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    multiple={false}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <a href="#" className="btn btn-link link-secondary" data-bs-dismiss="modal">
                  Cancel
                </a>
                <button type="submit" className="btn btn-primary ms-auto">
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction || (() => { })}
        title="Delete Confirmation"
        message={selectedIds.length > 0
          ? "Are you sure you want to delete the selected product categories? This action cannot be undone."
          : "Are you sure you want to delete this product category? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      {/* Fail Modal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default ProductCategory; 